import { createContext, FC, PropsWithChildren, useLayoutEffect, useMemo, useState } from 'react';
import { DesignCompany } from './types';

const themes = import.meta.glob('/src/styles/themes/*/index.css');

export type CompanyValuesApi = {
  company: string;
  isDefault: boolean;
  isExternal: boolean;
};

type CompanyActionsApi = {
  setCompany: (company: DesignCompany) => void;
};

export const CompanyValuesContext = createContext<CompanyValuesApi | null>(null);
export const CompanyActionsContext = createContext<CompanyActionsApi | null>(null);

export const CompanyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [company, setCompany] = useState<DesignCompany>((import.meta.env.VITE_COMPANY as DesignCompany) ?? 'audiencerate');
  const isDefault = company === 'audiencerate';
  const isExternal = company !== 'audiencerate';

  useLayoutEffect(() => {
    if (company) {
      const path = `/src/styles/themes/${company}/index.css`;
      const loadTheme = themes[path];

      if (loadTheme) {
        loadTheme();
      }

      document.documentElement.setAttribute('data-company', company);
    }
  }, [company]);

  const value = useMemo(
    () =>
      ({
        company,
        isDefault,
        isExternal,
      }) satisfies CompanyValuesApi,
    [company, isDefault, isExternal]
  );

  const actions = useMemo(
    () =>
      ({
        setCompany,
      }) satisfies CompanyActionsApi,
    [setCompany]
  );

  return (
    <CompanyValuesContext.Provider value={value}>
      <CompanyActionsContext.Provider value={actions}>{children}</CompanyActionsContext.Provider>
    </CompanyValuesContext.Provider>
  );
};
